<template>
    <div
        v-if="isDisplayed"
        ref="blockRef"
        class="block"
        :class="[
            { 'no-transition': blockTransition },
            ...getBlockClasses(block),
        ]"
        :style="getBlockStyles(block)"
    >
        <Component
            :is="component"
            v-bind="blockBindings"
            :error="error"
            v-on="blockListeners"
            @update:model-value="handleModelValueUpdated"
            @click="handleClick"
            @item-chosen="handleItemChosen"
            @download-document="handleDownloadDocument"
            @file:remove="handleFileRemove(block, $event)"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, watch, ref, inject, provide, shallowRef, type ComputedRef, onMounted, onUnmounted, nextTick, toRef, type Ref } from 'vue'
import type { BlockModelValue, BlockProps, ExtendedBlock } from './types'
import { omit } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import equals from 'fast-deep-equal/es6'
import { type getCustomPageBlockComponent } from 'widget-components/integrations/CustomPage/block-map'
import type { CustomCssVariables, DisplayDocumentPayload } from 'widget-components/types'
import useCustomCss from 'widget-components/composables/custom-css'
import useConditions from 'widget-components/composables/conditions/useConditions'
import { useEventBus } from '@vueuse/core'
import { emitBlockUpdate } from './utilities/emitBlockUpdate'
import { flattenBlocks } from 'widget-components/utilities/containers/flatten-blocks'
import { BLOCK_TYPES } from './consts'
import { isNestableBlock } from 'widget-components/utilities/containers/is-nestable-block'
import { getBlockClasses, getBlockStyles } from 'widget-components/integrations/CustomPage/utilities/getBlockStyles'
import { CONSUME_AUTOFOCUS_KEY } from 'widget-components/integrations/CustomPage/autofocus'
import type { InputDocumentItem } from 'widget-components/components/InputFileUpload/types'

const blockRef = ref<HTMLElement | undefined>()

const props = defineProps<BlockProps>()

const emit = defineEmits([
    'update:modelValue',
    'update:block',
    'click:preview-block',
    'download-document',
])

const blockId = computed(() => props.block.id)

const { isDisplayed, isDisabled: isDisabledCondition, conditionalStyle } = useConditions(
    blockId,
    toRef(props, 'parentIndex'),
    toRef(props, 'parentId'),
)

const getCustomPageBlockComponentFn = inject<getCustomPageBlockComponent>('getCustomPageBlockComponent')
const handleDownloadDocument = inject<(resource: DisplayDocumentPayload, id: string) => void>('handleDocumentDownload', () => {})
const handleFileRemove = inject<(block: ExtendedBlock, file: InputDocumentItem) => void>('handleFileRemove', () => {})
const customPageBuilder = inject<boolean>('customPageBuilder', false)
const isLoading = inject<ComputedRef<boolean>>('isLoading')
const isDisabled = inject<ComputedRef<boolean>>('isDisabled', computed(() => false))
const autofocusBlockIds = inject<Ref<Set<string>> | ComputedRef<Set<string>>>('autofocusBlockIds', ref(new Set<string>()))
const reportAutofocusDisplayed = inject<(id: string, displayed: boolean) => void>('reportAutofocusDisplayed')
const focusConsumed = ref(false)
provide(CONSUME_AUTOFOCUS_KEY, () => {
    focusConsumed.value = true
})

watch(isDisplayed, (displayed) => {
    if (props.block.id) {
        reportAutofocusDisplayed?.(props.block.id, displayed)
    }
}, { immediate: true })

onUnmounted(() => {
    if (props.block.id) {
        reportAutofocusDisplayed?.(props.block.id, false)
    }
})
const blockTransition = ref(true)

const component = shallowRef(getCustomPageBlockComponentFn?.(props.block))

const customCssVariables = ref<CustomCssVariables>()

const isBlockLoading = computed(() => {
    if (
        props.block.type === 'ACTION_BUTTON' &&
        ['NEXT', 'CLOSE'].includes(props.block.buttonType as string)
    ) {
        return isLoading?.value
    }

    if (['SELECTION_LIST', 'NAVIGATION'].includes(props.block.type)) {
        return isLoading?.value
    }

    return false
})

const applyCustomCss = () => {
    if (blockRef.value) {
        const { applyCssVariables, clearCssVariables, getBlockCssVariables } = useCustomCss()

        customCssVariables.value = { ...getBlockCssVariables(props.block), ...conditionalStyle.value }

        clearCssVariables(blockRef.value) // remove previous values to allow property removal
        applyCssVariables(customCssVariables.value, blockRef.value)
    }
}

onMounted(() => {
    applyCustomCss()
    setTimeout(() => {
        blockTransition.value = false
    }, 400)
})

watch(() => isDisplayed.value, (v) => {
    if (v) {
        // nextTick to have freshly rendered blockRef available
        nextTick(applyCustomCss)
    }
})

watch(conditionalStyle, () => {
    nextTick(applyCustomCss)
})

const handleModelValueUpdated = (val: BlockModelValue) => {
    emit('update:modelValue', val)
    emitBlockUpdate(props.block.blockPath, {
        id: props.block.id,
        error: undefined,
        currentValue: val,
    })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleAction = inject<(a: Record<string, any>) => void>('handleAction', () => undefined)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleTracker = inject<(a: Record<string, any>) => void>('handleTracker', () => undefined)

const handleClick = () => {
    if (blockBindings.value.type === 'ACTION_BUTTON' && !blockBindings.value.disabled && !blockBindings.value.loading) {
        handleAction({
            id: props.block.id,
            type: props.block.type,
            nextTask: props.block.nextTask,
        })
        handleTracker({
            actionName: 'actionButtonClicked',
            actionType: props.block.buttonType,
            taskUuid: props.block.nextTaskId,
        })
    }
}

const handleItemChosen = (_item: unknown) => {
    const item = _item as ExtendedBlock
    if (!blockBindings.value.disabled && !blockBindings.value.loading) {
        handleAction({
            id: props.block.id,
            type: props.block.type,
            nextTask: item.nextTask,
        })
        handleTracker({
            actionName: 'selectionListItemSelected',
            actionType: props.block.buttonType,
            taskUuid: props.block.nextTaskId,
        })
    }
}

const blockBindings = computed(() => (omit(['style'], {
    ...props.block,
    ...(props.block.internalData?.bindings || {}),
    blockHoisted: props.blockHoisted,
    disabled: isLoading?.value || isDisabledCondition.value || isDisabled.value,
    loading: isBlockLoading.value,
    customCssVariables: customCssVariables.value,
    modelValue: props.modelValue,
    focus: !focusConsumed.value && autofocusBlockIds.value.has(props.block.id || ''),
})))

const blockListeners = computed(() => {
    const listeners = props.block.internalData?.listeners || {}

    return Object.keys(listeners).reduce((acc, key) => {
        acc[key] = (...args: unknown[]) => {
            listeners[key].apply(undefined, [emit, props.block, ...args])
        }
        return acc
    }, {} as Record<string, unknown>)
})

const error = computed(() => props.block.error)

if (customPageBuilder) {
    const blockType = computed(() => {
        return props.block.type
    })

    watch(() => [blockType.value], () => {
        component.value = getCustomPageBlockComponentFn?.(props.block)
    }, { deep: true })

    watch( // update styles in builder
        () => [props.block.style, props.block.conditionalStyles],
        (v, prev) => {
            if (!equals(v, prev)) {
                applyCustomCss()
            }
        }, { deep: true, immediate: true },
    )
}

const RESET_VALUE_BY_BLOCK_TYPE: Partial<Record<ExtendedBlock['type'], unknown>> = {
    TOGGLE: false,
}

const DEFAULT_RESET_VALUE = ''

const getResetValue = (type: ExtendedBlock['type']) =>
    RESET_VALUE_BY_BLOCK_TYPE[type] ?? DEFAULT_RESET_VALUE

const resetBlockValue = (block: ExtendedBlock) => {
    if (block.blockPath) {
        emitBlockUpdate(block.blockPath, {
            initialValue: clone(block.currentValue),
            currentValue: getResetValue(block.type),
        })
    } else {
        useEventBus('block-unmounted-event').emit(block.id)
    }
}

const handleResetBlockValue = (block: ExtendedBlock) => {
    if (isNestableBlock(block)) {
        flattenBlocks(block.children as ExtendedBlock[])
            // filter form blocks that still have a non-empty value (it may have been reset already)
            .filter(b => b.internalData?.type === BLOCK_TYPES.FORM && b.currentValue !== getResetValue(b.type))
            .forEach(resetBlockValue)
    } else {
        resetBlockValue(block)
    }
}

watch(isDisplayed, (val) => {
    if (val) {
        return
    }
    handleResetBlockValue(props.block)
})
</script>

<style lang="sass" scoped>
@import '../../style/components/_container'

.block
    @extend %reset-container-variables

    font-weight: var(--tl-font-weight, normal)
    flex: 1
    min-width: var(--tl-container-min-width, 0)
    max-width: var(--tl-container-max-width, auto)

    &.no-transition :deep(*)
        transition: none !important

.preview
    &:hover
        background-color: var(--color-tl-blue-tint-extralight)
</style>
